<script setup>
import { Icon } from '@iconify/vue';

defineProps({
  job: { type: Object, required: true },
  cachedUrl: { type: String, default: '' },
  isBookmarked: { type: Boolean, default: false },
  timeAgo: { type: String, required: true },
  salary: { type: String, required: true },
});

defineEmits(['bookmark']);
</script>

<template>
  <div class="hidden sm:flex sm:flex-row gap-3 w-full items-stretch">
    <div
      class="w-24 h-24 bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-100 dark:border-gray-600 flex items-center justify-center p-2 shrink-0 self-center"
    >
      <img
        v-if="job.logo_perusahaan && cachedUrl"
        :src="cachedUrl"
        :alt="job.nama_perusahaan"
        class="w-full h-full object-contain"
        loading="lazy"
      />
      <span v-else class="text-4xl font-bold text-[#293146] dark:text-gray-200">{{
        job.nama_perusahaan?.charAt(0)
      }}</span>
    </div>

    <div class="min-w-0 grow flex flex-col justify-between py-1">
      <div class="flex justify-between items-start mb-1">
        <div class="min-w-0 pr-2">
          <h3
            class="font-bold text-[18px] text-gray-900 dark:text-white group-hover:text-slate-700 dark:group-hover:text-[#C5B47A] transition-colors leading-tight truncate"
          >
            {{ job.judul_pekerjaan }}
          </h3>
          <div class="flex items-center gap-1 mt-0.5">
            <p
              class="text-[15px] text-gray-600 dark:text-gray-300 font-medium truncate hover:text-slate-600 dark:hover:text-[#C5B47A] transition-colors"
            >
              {{ job.nama_perusahaan }}
            </p>
          </div>
        </div>
        <span
          class="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-[11px] font-bold px-1.5 py-0.5 rounded tracking-wider uppercase shrink-0"
          >{{ job.tipe_pekerjaan }}</span
        >
      </div>

      <div
        class="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1 text-[13px] font-medium text-gray-500 dark:text-gray-400"
      >
        <div class="flex items-center">
          <Icon
            icon="mdi:map-marker-outline"
            class="w-3.5 h-3.5 mr-1.5 text-gray-400 dark:text-gray-500 shrink-0"
          />
          <span class="truncate max-w-30">{{ job.lokasi_penempatan }}</span>
        </div>
        <div class="w-px h-3 bg-gray-200 dark:bg-gray-600"></div>
        <div class="flex items-center">
          <Icon
            icon="mdi:school-outline"
            class="w-3.5 h-3.5 mr-1.5 text-gray-400 dark:text-gray-500 shrink-0"
          />
          <span class="truncate max-w-25">{{ job.minimal_pendidikan }}</span>
        </div>
        <div class="w-px h-3 bg-gray-200 dark:bg-gray-600"></div>
        <div class="flex items-center">
          <Icon
            icon="mdi:account-outline"
            class="w-3.5 h-3.5 mr-1.5 text-gray-400 dark:text-gray-500 shrink-0"
          />
          <span>{{ job.jenis_kelamin }}</span>
        </div>
        <div class="w-px h-3 bg-gray-200 dark:bg-gray-600"></div>
        <div class="flex items-center text-emerald-700 dark:text-emerald-400 font-bold">
          <Icon icon="mdi:cash-multiple" class="w-3.5 h-3.5 mr-1.5 shrink-0" />
          <span>{{ salary }}</span>
        </div>
      </div>

      <div class="flex items-center text-xs text-gray-400 dark:text-gray-500 mt-auto">
        <Icon icon="mdi:clock-outline" class="w-3.5 h-3.5 mr-1" />
        <span>{{ timeAgo }}</span>
      </div>
    </div>

    <div
      class="flex flex-col justify-between items-end shrink-0 pl-3 border-l border-gray-100 dark:border-gray-700 self-stretch"
    >
      <button
        @click.prevent="$emit('bookmark')"
        class="transition-colors"
        :class="
          isBookmarked
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'
        "
      >
        <Icon :icon="isBookmarked ? 'mdi:bookmark' : 'mdi:bookmark-outline'" class="w-6 h-6" />
      </button>

      <span
        class="bg-[#293146] dark:bg-[#C5B47A] text-white dark:text-gray-900 font-bold rounded text-xs px-3 py-1 hover:bg-[#3b4660] dark:hover:bg-[#C5B47A] transition-all shadow-sm active:scale-95 cursor-pointer whitespace-nowrap mt-auto"
      >
        {{ $t('home.applyBtn') }}
      </span>
    </div>
  </div>
</template>
